import clsx from 'clsx';
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

interface DialogContextProps {
  openDialog: (render: () => ReactNode) => void;
  pushDialog: (render: () => ReactNode) => void;
  popDialog: () => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextProps | null>(null);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [stack, setStack] = useState<ReactNode[]>([]);
  const [show, setShow] = useState(false);

  const nodeRef = useRef<HTMLDivElement>(null);

  const openDialog = useCallback((render: () => ReactNode) => {
    setStack([render()]);
    setShow(true);
  }, []);

  const pushDialog = useCallback((render: () => ReactNode) => {
    setStack((prev) => [...prev, render()]);
    setShow(true);
  }, []);

  const popDialog = useCallback(() => {
    setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev));
    if (stack.length <= 1) setShow(false);
  }, [stack.length]);

  const closeDialog = useCallback(() => {
    setShow(false);
  }, []);

  const current = stack[stack.length - 1];

  return (
    <DialogContext.Provider value={{ openDialog, pushDialog, popDialog, closeDialog }}>
      {children}

      <CSSTransition
        in={show && !!current}
        timeout={{ enter: 220, exit: 150 }}
        classNames="dialog"
        mountOnEnter
        unmountOnExit
        nodeRef={nodeRef}
        onExited={() => setStack([])}
      >
        <div ref={nodeRef} className="fixed inset-0 z-50 flex items-center justify-center">
          <div onClick={closeDialog} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            className={clsx(
              'relative z-10 bg-background/95 dark:bg-card/60 backdrop-blur-md rounded-2xl shadow-2xl px-8 pt-8 pb-8 min-w-[600px] max-w-[90%] max-h-[90vh] overflow-auto transform transition-all duration-300 ease-out border border-border/50',
            )}
          >
            {stack.length > 1 ? (
              <div className="flex items-center justify-between mb-6 -mt-2">
                <button
                  onClick={() => setStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))}
                  className="text-xs font-bold uppercase tracking-widest text-muted-foreground/80 hover:text-primary transition-colors cursor-pointer"
                >
                  ← Назад
                </button>
                <button
                  onClick={closeDialog}
                  aria-label="Закрыть диалог"
                  className="text-lg text-muted-foreground/40 hover:text-destructive transition-colors cursor-pointer"
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                onClick={closeDialog}
                aria-label="Закрыть диалог"
                className="text-lg text-muted-foreground/40 hover:text-destructive transition-colors cursor-pointer absolute right-6 top-6 z-20"
              >
                ✕
              </button>
            )}

            {current}

            {/*<div className="flex justify-end mt-6">*/}
            {/*  <button*/}
            {/*    onClick={closeDialog}*/}
            {/*    className="px-4 cursor-pointer py-2 text-sm font-medium bg-gray-100  rounded-md hover:bg-gray-200 transition text-black"*/}
            {/*  >*/}
            {/*    Отмена*/}
            {/*  </button>*/}
            {/*</div>*/}
          </div>
        </div>
      </CSSTransition>
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within DialogProvider');
  return ctx;
};
